// src/services/apiService.js (修正版本 - 使用 authService)
import axios from 'axios';

/**
 * 確定 API 基礎 URL - 針對你的 NGINX 配置優化
 */
const determineApiUrl = () => {
  // 優先使用環境變數，但要檢查是否合理
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    
    // 如果當前是 HTTPS 站點，但環境變數是 localhost，則不能使用
    if (window.location.protocol === 'https:' && envUrl.includes('localhost')) {
      // 降級至同域名 API
    } else {
      // 確保 URL 包含 /api 後綴
      return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
    }
  }
  
  // 本地開發環境
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5001/api';
  }
  
  // 生產環境 - 使用相同域名但不同路徑
  // 根據你的 NGINX 配置，API 在 /api/ 路徑下
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;

  // 使用同一域名的 /api 路徑
  return `${protocol}//${hostname}/api`;
};

// 設定 API 基礎 URL
export const API_URL = determineApiUrl();

// 🔥 重要：延遲導入 authService 以避免循環依賴
let authService = null;
const getAuthService = () => {
  if (!authService) {
    try {
      authService = require('./authService').default;
    } catch (error) {
      // 備用方法：直接檢查 localStorage
      return {
        getToken: () => {
          const tokenKeys = ['authToken', 'token', 'accessToken', 'jwt', 'jwtToken'];
          for (const key of tokenKeys) {
            const token = localStorage.getItem(key);
            if (token) return token;
          }
          return null;
        }
      };
    }
  }
  return authService;
};

/**
 * 獲取認證頭部 - 使用 authService
 * @returns {Object} 包含認證令牌的頭部
 */
export const getAuthHeaders = () => {
  // 🔥 修正：使用 authService.getToken() 而不是直接訪問 localStorage
  const auth = getAuthService();
  const token = auth.getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * 創建配置好的 axios 實例
 * @returns {Object} 配置好的 axios 實例
 */
export const createApiClient = () => {
  const apiClient = axios.create({
    baseURL: API_URL,
    headers: getAuthHeaders(),
    timeout: 30000, // 30 秒超時
    withCredentials: true // 關鍵: 啟用跨域請求時發送憑證（cookies）
  });

  // 添加請求攔截器以包含認證令牌
  apiClient.interceptors.request.use(
    config => {
      // 🔥 修正：使用 authService.getToken() 而不是直接訪問 localStorage
      const auth = getAuthService();
      const token = auth.getToken();
      
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      
      // 添加時間戳以防止緩存（僅適用於 GET 請求）
      if (config.method === 'get') {
        config.params = config.params || {};
        config.params._ts = Date.now();
      }
      
      return config;
    },
    error => Promise.reject(error)
  );
  
  // 添加響應攔截器處理常見錯誤
  apiClient.interceptors.response.use(
    response => response,
    error => {
      // 未授權錯誤處理 — 重導至登入頁
      if (error.response?.status === 401) {
        if (window.location.pathname !== '/login') {
          const auth = getAuthService();
          if (auth.logout) {
            auth.logout();
          } else {
            localStorage.removeItem('authToken');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }

          localStorage.setItem('redirectAfterLogin', window.location.pathname);

          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }

      return Promise.reject(error);
    }
  );

  return apiClient;
};

/**
 * 執行 API 請求
 * @param {String} method - HTTP 方法 (get, post, put, delete, patch)
 * @param {String} endpoint - API 端點路徑
 * @param {Object} data - 請求數據 (適用於 POST, PUT, PATCH)
 * @param {Boolean} isTest - 是否為測試請求 (無需認證)
 * @param {Object} customConfig - 自定義配置
 * @returns {Promise} Axios 請求的 Promise
 */
export const apiRequest = async (method, endpoint, data = null, isTest = false, customConfig = {}) => {
  try {
    // 準備頭部
    const headers = getAuthHeaders();
    
    // 如果是測試請求，則不添加認證頭部
    if (isTest) {
      delete headers['Authorization'];
    }
    
    // 合併頭部和自定義配置
    const config = { 
      headers,
      withCredentials: true, // 啟用 Cookie
      ...customConfig 
    };
    
    // 在處理 FormData 時，讓瀏覽器設定正確的內容類型
    if (data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // 添加時間戳以防止緩存（僅適用於 GET 請求）
    if (method.toLowerCase() === 'get') {
      config.params = config.params || {};
      config.params._ts = Date.now();
    }
    
    // 格式化 URL - 確保沒有雙斜杠
    let apiUrl = !endpoint.startsWith('http')
      ? `${API_URL}/${endpoint.replace(/^\/+/, '')}` // 相對 URL
      : endpoint; // 絕對 URL

    // 代理商公司切換：自動附加 companyId 參數
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAgency = user.role === 'agency_admin' || user.role === 'agency_staff';
    const agencySelectedId = localStorage.getItem('agencySelectedCompanyId');
    const ownCompanyId = String(user.companyId || user.company_id || '');

    if (isAgency && agencySelectedId && agencySelectedId !== ownCompanyId) {
      if (!apiUrl.includes('companyId=')) {
        const separator = apiUrl.includes('?') ? '&' : '?';
        apiUrl = `${apiUrl}${separator}companyId=${agencySelectedId}`;
      }
    }

    // body-parser strict mode 只接受 object/array，null 會被序列化為 "null" 導致 500
    const body = data ?? {};

    // 執行請求
    let response;
    switch (method.toLowerCase()) {
      case 'get':
        response = await axios.get(apiUrl, config);
        break;
      case 'post':
        response = await axios.post(apiUrl, body, config);
        break;
      case 'put':
        response = await axios.put(apiUrl, body, config);
        break;
      case 'delete':
        response = await axios.delete(apiUrl, config);
        break;
      case 'patch':
        response = await axios.patch(apiUrl, body, config);
        break;
      default:
        throw new Error(`不支援的方法: ${method}`);
    }
    
    return response;
  } catch (error) {
    // 重新拋出錯誤以供調用方處理
    throw error;
  }
};

/**
 * 測試 API 連接
 * @returns {Promise<boolean>} 連接是否成功
 */
export const testApiConnection = async () => {
  try {
    await apiRequest('get', 'health', null, true);
    return true;
  } catch (error) {
    return false;
  }
};

// Add this function to your apiService.js before the final export

/**
 * 設置公司子域名 - 用於 LineConfigPage
 * @param {String} companyId - 公司 ID
 * @param {String} subdomain - 子域名
 * @returns {Promise} API 響應
 */
export const setCompanySubdomain = async (companyId, subdomain) => {
  try {
    if (!companyId) throw new Error('公司 ID 是必須的');
    if (!subdomain) throw new Error('子域名是必須的');

    const response = await apiRequest('put', `companies/${companyId}`, { subdomain });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || '設置公司子域名失敗');
  }
};



// 導出服務
export default {
  API_URL,
  getAuthHeaders,
  createApiClient,
  apiRequest,
  testApiConnection,
  setCompanySubdomain
};